/**
 * Transfer between two customer accounts (D-073; old `handleCustomerTransfer` App.tsx:19604-19902) as
 * `customer_transfer` in one currency: never above the source balance (engine), the source moves toward zero; when a
 * cross-type transfer would take the recipient below zero the engine answers `SURPLUS_ACCOUNT_REQUIRED` and the
 * surplus options appear (recipient's opposite account / existing / new — D-073 (2)); one atomic entry; the personal
 * PIN replaces the on-screen 4-digit code and the operations code (D-073 (5)). "تسجيل مع طباعة" prints an A4 transfer
 * record after posting (the 80 mm voucher is B5, D-064).
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CustomerTransferModal, type TransferSide } from "@faylasuf/ui";
import { PRINT_ROOT_CLASS, StatementA4, printDocument } from "@faylasuf/print";
import type { CustomerAccountRow, PreviewResult } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { engineErrors, errorMessage } from "../../app/errors.js";
import { useGateway } from "../../app/operations.js";
import { useToast } from "../../app/toast.js";
import { officeDate } from "../wallets/StatementContainer.js";
import { accountIdOf, balanceOf, fmt, kindLabel, positive } from "./format.js";
import { EMPTY_SURPLUS, resolveSurplus, surplusTargets, type SurplusState } from "./surplus.js";
import { usePrintOffice } from "../../app/printOffice.js";

export interface TransferPreset {
  readonly fromId: string;
  readonly currency?: "IQD" | "USD";
  readonly amount?: string;
  readonly note?: string;
}

export function TransferContainer({ rows, usdHidden, preset, onClose }: { rows: readonly CustomerAccountRow[]; usdHidden: boolean; preset: TransferPreset | null; onClose: () => void }) {
  const { t, locale, now } = useApp();
  const { session } = useSession();
  const printOffice = usePrintOffice(); // [C29] F-286: identity lines in the A4 header
  const toast = useToast();
  const gateway = useGateway();
  const [currency, setCurrency] = useState<"IQD" | "USD">(preset?.currency ?? "IQD");
  const [from, setFrom] = useState<TransferSide>({ id: preset?.fromId ?? "", typeFilter: "all", search: "" });
  const [to, setTo] = useState<TransferSide>({ id: "", typeFilter: "all", search: "" });
  const [amount, setAmount] = useState(preset?.amount ?? "");
  const [note, setNote] = useState(preset?.note ?? "");
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [surplusNeeded, setSurplusNeeded] = useState<string | null>(null);
  const [surplus, setSurplus] = useState<SurplusState>(EMPTY_SURPLUS);
  const [busy, setBusy] = useState(false);
  const [printDoc, setPrintDoc] = useState<{ from: string; to: string; amount: string; note: string; doc: string } | null>(null);
  const seq = useRef(0);

  const fromRow = rows.find((r) => r.id === from.id);
  const toRow = rows.find((r) => r.id === to.id);
  const input = fromRow && toRow && positive(amount)
    ? { fromAccountId: accountIdOf(fromRow, currency), toAccountId: accountIdOf(toRow, currency), amount, ...(note.trim() ? { note: note.trim() } : {}) }
    : null;

  useEffect(() => {
    setPreview(null);
    setSurplusNeeded(null);
    if (!input) return;
    const my = ++seq.current;
    const h = setTimeout(() => {
      gateway.preview("customer_transfer", input).then(
        (pv) => {
          if (my === seq.current) setPreview(pv);
        },
        (e: unknown) => {
          if (my !== seq.current) return;
          const s = engineErrors(e).find((x) => x.code === "SURPLUS_ACCOUNT_REQUIRED");
          if (s) setSurplusNeeded(String(s.params["surplus"] ?? ""));
        },
      );
    }, 350);
    return () => clearTimeout(h);
  }, [from.id, to.id, currency, amount, note]);

  // The recipient decreases: a debt recipient's excess goes to a trust account and vice versa.
  const targetKind = toRow?.kind === "debt" ? "trust" : "debt";

  useEffect(() => {
    if (!printDoc) return;
    const done = () => {
      setPrintDoc(null);
      onClose();
    };
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [printDoc, onClose]);

  const submit = async (print: boolean) => {
    if (!input || !fromRow || !toRow) {
      toast(t("cust.transfer_pick_both"), "error");
      return;
    }
    let body: Record<string, unknown> = input;
    let newAccounts: Parameters<typeof gateway.previewWithNew>[2] = [];
    let newName: string | null = null;
    if (surplusNeeded !== null) {
      const r = resolveSurplus(surplus, toRow, rows, targetKind, currency);
      if (!r.ok) {
        toast(t(r.messageKey), "error");
        return;
      }
      body = { ...input, surplus: { kind: "account", accountId: r.accountId } };
      newAccounts = r.newAccounts;
      newName = r.newName;
    }
    setBusy(true);
    try {
      let pv = surplusNeeded === null ? preview : null;
      if (!pv) {
        try {
          pv = await gateway.previewWithNew("customer_transfer", body, newAccounts);
        } catch (e) {
          toast(errorMessage(t, e), "error");
          return;
        }
      }
      const res = await gateway.commitPreview(pv, {
        pinMessage: newName ? t("cust.pin_new_account", { name: newName }) : t("cust.pin_transfer", { from: fromRow.name, to: toRow.name }),
        successKey: "toast.transfer_posted",
      });
      if (!res) return;
      if (print && res.status === "posted") {
        setPrintDoc({ from: fromRow.name, to: toRow.name, amount: `${fmt(amount)} ${currency === "USD" ? "$" : t("unit.iqd")}`, note: note.trim(), doc: res.operationId.slice(-6) });
      } else onClose();
    } catch {
      /* toasted by the gateway */
    } finally {
      setBusy(false);
    }
  };

  const options = rows.map((r) => {
    const bal = balanceOf(r, currency);
    return { id: r.id, name: r.name, accountNumber: r.accountNumber, kind: r.kind, balanceLabel: fmt(bal), negative: bal.startsWith("-") };
  });

  return (
    <>
      <CustomerTransferModal
        currency={currency}
        onCurrencyChange={setCurrency}
        usdHidden={usdHidden}
        options={options}
        from={from}
        to={to}
        onFromChange={(p) => setFrom((s) => ({ ...s, ...p }))}
        onToChange={(p) => setTo((s) => ({ ...s, ...p }))}
        amount={amount}
        onAmountChange={setAmount}
        note={note}
        onNoteChange={setNote}
        surplus={
          surplusNeeded !== null && toRow
            ? {
                type: toRow.kind === "debt" ? "debt_overpaid" : "trust_overdrawn",
                recipientName: toRow.name,
                message: t("cust.transfer_surplus_msg", { amount: `${fmt(amount)} ${currency}`, original: `${fmt(balanceOf(toRow, currency))} ${currency}`, surplus: `${fmt(surplusNeeded)} ${currency}` }),
              }
            : null
        }
        surplusProps={{
          choice: surplus.choice,
          onChoice: (choice) => setSurplus((s) => ({ ...s, choice })),
          targets: surplusTargets(rows, [to.id, from.id], targetKind, currency, t),
          targetId: surplus.targetId,
          onTarget: (targetId) => setSurplus((s) => ({ ...s, targetId })),
          newName: surplus.newName,
          onNewName: (newName) => setSurplus((s) => ({ ...s, newName })),
        }}
        busy={busy}
        onSubmit={(p) => void submit(p)}
        onClose={onClose}
      />
      {gateway.pin.dialog}
      {printDoc &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            <StatementA4
              locale={locale}
              title={t("customerTransfer")}
              customerName={`${printDoc.from} ← ${printDoc.to}`}
              dateLabel={officeDate(now(), session.office.timeZone)}
              docNumber={printDoc.doc}
              office={printOffice}
              columns={[
                { header: t("fromCustomer"), key: "from" },
                { header: t("toCustomer"), key: "to" },
                { header: t("transferAmount"), key: "amount" },
                { header: t("transferNote"), key: "note" },
              ]}
              rows={[{ from: `${printDoc.from} (${fromRow ? kindLabel(t, fromRow.kind) : ""})`, to: `${printDoc.to} (${toRow ? kindLabel(t, toRow.kind) : ""})`, amount: printDoc.amount, note: printDoc.note || "-" }]}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
